import { FC, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { useTranslation } from 'react-i18next';
import qs from 'query-string';
import { FaDownload, FaExclamationCircle } from 'react-icons/fa';
import Form from 'components/form/Form';
import FormGroup from 'components/form/FormGroup';
import FormButtons from 'components/form/FormButtons';
import Button from 'components/Button';
import Alert from 'react-bootstrap/Alert';
import API from 'core/Api';

interface IProps {
  methods: any;
  data: any;
  onSubmit: (data: any) => void;
  showDownloadBtn: boolean;
  reportParams?: any;
  hideDownloadBtn?: any;
}

interface IDataType {
  [key: string]: string | number | boolean;
}

const ReportForm: FC<IProps> = ({ methods, data, onSubmit, showDownloadBtn, reportParams, hideDownloadBtn }) => {
  const { t } = useTranslation();
  const { formState } = methods;
  const { isSubmitting } = formState;
  const [employees, setEmployees] = useState(data?.user || []);
  const [reportBtnLoading, setReportBtnLoading] = useState(false);

  // Get navigation state and extract employee data (fallback to empty object if no state is provided)
  const location = useLocation<{ employeeId: number; employeeCreatedAt: Date }>();
  const { employeeId, employeeCreatedAt } = location.state || {};
  // Find the matching employee option from the available employees list
  // This is used to preselect the employee in the filter component
  const defaultEmployee = employees.filter((emp: IDataType) => emp.value === employeeId);

  useEffect(() => {
    methods.setValue(
      'created_at_from',
      employeeCreatedAt ? new Date(employeeCreatedAt) : new Date(new Date().getFullYear(), 0, 1)
    );
    methods.setValue('created_at_to', new Date());
  }, []);

  const groups = methods.watch('group_id');
  const categories = methods.watch('category_id');
  const users = methods.watch('user_id');
  const creators = methods.watch('creator_id');
  const createdAtFrom = methods.watch('created_at_from');
  const createdAtTo = methods.watch('created_at_to');
  const searchField = methods.watch('search');

  // Hide download button on filters change
  // in order to generate new report firstly
  useEffect(() => {
    hideDownloadBtn();
  }, [groups, categories, users, creators, createdAtFrom, createdAtTo, searchField]);

  useEffect(() => {
    const groupIds = groups?.map((gr: any) => gr.value);

    /**
     * When a group is selected, filter employee options
     * to only employees from groups selected and
     * automatically select all employees
     *
     * If no group selected - clear selected employees
     */
    if (groupIds) {
      const filteredEmployees = data?.user.filter((empl: any) => {
        /**
         * If employee doesn't have any groups check if "No group"
         * is selected which value is set to 0 from BE
         */
        if (!empl.groups.length) return groupIds.includes('null');

        const isInSelectedGroups = empl.groups.reduce((flag: boolean, currGr: number) => {
          return flag || groupIds.includes(currGr);
        }, false);
        return isInSelectedGroups;
      });

      setEmployees(filteredEmployees);
      methods.setValue('user_id', filteredEmployees);
    } else if (defaultEmployee) {
      methods.setValue('user_id', defaultEmployee);
    } else {
      setEmployees(data?.user);
      methods.setValue('user_id', null);
    }
  }, [groups]);

  return (
    <Form
      methods={methods}
      btnLabel={t('reports.submitBtn')}
      hideAllBtns
      onSubmit={onSubmit}
    >
      {employeeId && defaultEmployee.length === 0 && (
        <Row>
          <Col lg={12}>
            <Alert variant="danger">
              <FaExclamationCircle style={{ marginRight: 6 }} />
              <p className="mb-0 d-inline">{t('reports.noEmployeeDocumentsOrAccess')}</p>
            </Alert>
          </Col>
        </Row>
      )}
      <Row>
        <Col lg={4}>
          <FormGroup
            controlId="formGroupCategories"
            label={t('reports.categories')}
            type="select"
            as="select"
            name="category_id"
            isMulti
            isClearable
            isSearchable
            allowSelectAll
            selectOptions={data?.category}
          />
        </Col>
        <Col lg={4}>
          <FormGroup
            controlId="formGroupGroups"
            label={t('reports.groups')}
            type="select"
            as="select"
            name="group_id"
            isMulti
            isClearable
            isSearchable
            allowSelectAll
            selectOptions={data?.group}
          />
        </Col>
        <Col lg={4}>
          <FormGroup
            controlId="formGroupEmployees"
            label={t('reports.employees')}
            type="select"
            as="select"
            name="user_id"
            isMulti
            isClearable
            isSearchable
            allowSelectAll
            showTotal
            selectOptions={employees}
            limitOptions
          />
        </Col>
      </Row>
      <Row>
        <Col lg={4}>
          <FormGroup
            controlId="formGroupCreator"
            label={t('reports.author')}
            type="select"
            as="select"
            name="creator_id"
            isMulti
            isClearable
            isSearchable
            allowSelectAll
            selectOptions={data?.creator}
          />
        </Col>
        <Col lg={4}>
          <Row>
            <Col>
              <FormGroup
                controlId="formGroupFromDate"
                label={t('reports.fromDate')}
                type="datepicker"
                name="created_at_from"
                maxDate={new Date()}
                dateFormat="dd.MM.yyyy"
                showMonthDropdown
                showYearDropdown
              />
            </Col>
            <Col>
              <FormGroup
                controlId="formGroupToDate"
                label={t('reports.toDate')}
                type="datepicker"
                name="created_at_to"
                maxDate={new Date()}
                dateFormat="dd.MM.yyyy"
                showMonthDropdown
                showYearDropdown
              />
            </Col>
          </Row>
        </Col>
        <Col lg={4}>
          <FormGroup
            controlId="formGroupName"
            label={t('reports.docName')}
            type="text"
            name="search"
          />
        </Col>
      </Row>
      <div className="d-flex flex-column flex-sm-row justify-content-center mb-5">
        <FormButtons
          btnLabel={t('reports.submitBtn')}
          isSubmitting={isSubmitting}
          hideCancelBtn
        />
        {showDownloadBtn && (
          <div className="pt-2 pl-sm-3 text-center text-sm-auto">
            <Button
              btnType="secondary"
              disabled={reportBtnLoading}
              loading={reportBtnLoading}
              onClick={() => {
                setReportBtnLoading(true);
                const queryString = qs.stringify(
                  {
                    ...reportParams,
                  },
                  { arrayFormat: 'bracket', skipNull: true }
                );
                API.get(`reports/documents/download?${queryString}`).then(res => {
                  setReportBtnLoading(false);
                  window.open(res.data.url, '_blank');
                });
              }}
            >
              <FaDownload
                size={14}
                className="mr-2"
              />
              {t('reports.downloadReport')}
            </Button>
          </div>
        )}
      </div>
    </Form>
  );
};

export default ReportForm;
